import React, { useEffect, useRef, useMemo } from 'react';
import { AddTapsToRunToGoalV2Zone, ResolveMediaUrl } from './../../../utilities/BL/RunToGoal';

// =========================================================================
// DICCIONARIO DE MÁSCARA VISUAL (FALLBACK PRESET)
// Traduce el nombre del backend al nombre de la campaña de La Moderna / Xolos
// =========================================================================
const getVisualZoneName = (realZoneName) => {
    if (!realZoneName) return '';
    const normalized = realZoneName.toLowerCase().trim();

    const aliases = {
        'zona1': 'Xolo Mayor Rojo',
        'zona2': 'Xolo Mayor Blanco',
        'zona 1': 'Xolo Mayor Rojo',
        'zona 2': 'Xolo Mayor Blanco'
    };

    return aliases[normalized] || realZoneName;
};

/**
 * Extrae la lista de zonas configuradas en el tema (como array de objetos).
 */
const extractThemeZones = (th) => {
    if (!th || typeof th !== 'object') return [];
    if (Array.isArray(th.zones) && th.zones.length > 0) return th.zones;
    if (Array.isArray(th.Zones) && th.Zones.length > 0) return th.Zones;
    if (Array.isArray(th.runners) && th.runners.length > 0) return th.runners;
    const rawJson = th.zonesConfigJson || th.ZonesConfigJson;
    if (typeof rawJson === 'string' && rawJson.trim().length > 0) {
        try {
            const parsed = JSON.parse(rawJson);
            if (Array.isArray(parsed)) return parsed;
        } catch (e) {
            // Ignorar si no es JSON válido
        }
    }
    return [];
};

/**
 * COMPONENTE PWA MÓVIL DEL AFICIONADO: TIRO A GOL V2 (HU-05)
 * Soporta personalización completa por tema (colores, fondos, avatares, iconos de tap)
 * y buffer mutable de alta velocidad con feedback háptico.
 */
const GoalV2 = ({ matchState, config, theme, winnerZone, userZone }) => {
    // 1. Extraer tema activo desde props / estado
    const activeTheme = useMemo(() => {
        return theme || config?.theme || matchState?.theme || matchState?.themeSnapshot || config || {};
    }, [theme, config, matchState]);

    const themeZones = useMemo(() => {
        return extractThemeZones(activeTheme);
    }, [activeTheme]);

    // 2. Mapear zona del usuario a la configuración del tema
    const normalizedUserZone = String(userZone || '').trim().toLowerCase();
    const matchedZoneConfig = useMemo(() => {
        if (!normalizedUserZone || themeZones.length === 0) return null;
        return themeZones.find(z => {
            const zid = String(z.zoneId || z.ZoneId || z.id || z.name || '').trim().toLowerCase();
            return zid === normalizedUserZone ||
                   zid === normalizedUserZone.replace('zona ', 'zona') ||
                   zid === normalizedUserZone.replace(' ', '');
        }) || null;
    }, [normalizedUserZone, themeZones]);

    // Propiedades visuales de la zona asignada
    const visualZoneName = matchedZoneConfig?.name || matchedZoneConfig?.Name || getVisualZoneName(userZone);

    // 3. Fases y estado de partida
    const currentPhase = String(matchState?.phase || matchState?.currentPhase || 'WAITING').toUpperCase();
    const activeMatchIdentifier = matchState?.matchId || matchState?.matchIdentifier || config?.matchId;
    const activeWinnerZone = winnerZone || matchState?.winner || matchState?.winnerZone || null;

    const tapsBatchSize = config?.tapsPorTick || activeTheme?.tapsPorTick || 10;
    const minimumFlushTimeMilliseconds = config?.minUpdateMs || activeTheme?.minUpdateMs || 150;

    const memoryBufferReference = useRef({
        tapsAccumulated: 0,
        dispatchTimerId: null,
        isCurrentlySending: false
    });

    const isPlayerAllowedToTap = Boolean(
        userZone &&
        activeMatchIdentifier &&
        !activeWinnerZone &&
        currentPhase === 'RUNNING'
    );

    // 4. Despacho y buffer mutable de taps
    const flushTapsToBackendAPI = async () => {
        const memoryBuffer = memoryBufferReference.current;

        if (memoryBuffer.dispatchTimerId) {
            clearTimeout(memoryBuffer.dispatchTimerId);
            memoryBuffer.dispatchTimerId = null;
        }

        if (memoryBuffer.isCurrentlySending || memoryBuffer.tapsAccumulated <= 0 || !activeMatchIdentifier || !userZone) {
            return;
        }

        const tapsToDispatch = memoryBuffer.tapsAccumulated;
        memoryBuffer.tapsAccumulated = 0;
        memoryBuffer.isCurrentlySending = true;

        try {
            await AddTapsToRunToGoalV2Zone({
                matchIdentifier: activeMatchIdentifier,
                zoneIdentifier: userZone,
                tapsObtained: tapsToDispatch
            });
        } catch {
            memoryBuffer.tapsAccumulated += tapsToDispatch;
        } finally {
            memoryBuffer.isCurrentlySending = false;
            if (memoryBuffer.tapsAccumulated > 0 && !memoryBuffer.dispatchTimerId) {
                memoryBuffer.dispatchTimerId = setTimeout(() => {
                    memoryBuffer.dispatchTimerId = null;
                    flushTapsToBackendAPI();
                }, minimumFlushTimeMilliseconds);
            }
        }
    };

    const handleScreenTap = () => {
        if (!isPlayerAllowedToTap) return;

        // Feedback háptico corto (15ms)
        if (navigator.vibrate) {
            try {
                navigator.vibrate(15);
            } catch {
                // Silencioso en navegadores sin soporte de vibración
            }
        }

        const memoryBuffer = memoryBufferReference.current;
        memoryBuffer.tapsAccumulated++;

        if (memoryBuffer.tapsAccumulated >= tapsBatchSize) {
            if (memoryBuffer.dispatchTimerId) {
                clearTimeout(memoryBuffer.dispatchTimerId);
                memoryBuffer.dispatchTimerId = null;
            }
            flushTapsToBackendAPI();
            return;
        }

        if (!memoryBuffer.dispatchTimerId) {
            memoryBuffer.dispatchTimerId = setTimeout(() => {
                memoryBuffer.dispatchTimerId = null;
                flushTapsToBackendAPI();
            }, minimumFlushTimeMilliseconds);
        }
    };

    useEffect(() => {
        if (currentPhase === 'FINISHED') {
            flushTapsToBackendAPI();
        }
        return () => {
            flushTapsToBackendAPI();
        };
    }, [currentPhase]);

    // 5. Resolución de nombre de zona para mensajes de victoria
    const getVisualNameForZone = (realZoneName) => {
        if (!realZoneName) return '';
        const normalized = realZoneName.toLowerCase().trim();
        const matched = themeZones.find(z => {
            const zid = String(z.zoneId || z.ZoneId || z.id || z.name || '').trim().toLowerCase();
            return zid === normalized ||
                   zid === normalized.replace('zona ', 'zona') ||
                   zid === normalized.replace(' ', '');
        });
        if (matched && (matched.name || matched.Name)) {
            return matched.name || matched.Name;
        }
        return getVisualZoneName(realZoneName);
    };

    const renderWinnerText = (winnerRaw) => {
        if (!winnerRaw || winnerRaw === 'tie') return '¡Ha habido un empate!';

        if (winnerRaw.startsWith('tie_partial:')) {
            const tiedZones = winnerRaw.split(':')[1].split(' y ');
            const uniqueAliases = [...new Set(tiedZones.map(z => getVisualNameForZone(z).toUpperCase()))];
            return `¡Empataron ${uniqueAliases.join(' y ')}!`;
        }

        const winnerVisual = getVisualNameForZone(winnerRaw);
        if (activeTheme.winnerTextTemplate) {
            return activeTheme.winnerTextTemplate
                .replace('{GANADOR}', winnerVisual)
                .replace('{ZONE_NAME}', winnerVisual)
                .replace('{ZONE_ID}', winnerRaw);
        }

        return `¡Ha ganado el ${winnerVisual}!`;
    };

    // 6. Configuración de fondo y botón
    const fanBgUrl = activeTheme.fanBackgroundUrl || (activeTheme.fanBackgroundType !== 'color' ? '/img/games/runtogoal/redbull/background.jpg' : null);
    const resolvedFanBgUrl = fanBgUrl ? ResolveMediaUrl(fanBgUrl) : null;
    const fanBgColor = activeTheme.fanBackgroundColor || '#070707';

    const fanButtonIcon = activeTheme.fanButtonIcon || activeTheme.fanTapButtonUrl || activeTheme.tapButtonUrl || '';
    const isIconClass = Boolean(
        fanButtonIcon &&
        (fanButtonIcon.startsWith('bi ') || fanButtonIcon.startsWith('bi-') || fanButtonIcon.startsWith('fa ') || fanButtonIcon.startsWith('fa-'))
    );
    const buttonImageUrl = !isIconClass
        ? (fanButtonIcon ? ResolveMediaUrl(fanButtonIcon) : '/img/games/runtogoal/redbull/tap.png')
        : null;

    return (
        <div className="gfan" style={{ backgroundColor: fanBgColor }}>
            <div className='gfan__background'>
                {resolvedFanBgUrl && (
                    <img src={resolvedFanBgUrl} className='gfan__background__Image' alt="Fondo" />
                )}
            </div>

            <header className="container Fan__Goal__GameHeader">
                <div className="w-100 d-flex justify-content-center">
                    <div className="text-end mt-3 me-3">
                        <p className="gfan__controls__who mb-0 text-white font-weight-bold text-center" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                            TU PERSONAJE:
                        </p>
                        <h4 className="text-uppercase text-danger fw-bold text-center" style={{ textShadow: '2px 2px 4px rgba(255,255,255,0.9)' }}>
                            {visualZoneName || 'No Asignada'}
                        </h4>
                    </div>
                </div>
            </header>

            <div className='gfan__controls'>
                {activeWinnerZone && (
                    <p className="gfan__controls__tap__Text" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                        {renderWinnerText(activeWinnerZone)}
                    </p>
                )}

                {!activeWinnerZone && (
                    <>
                        {isPlayerAllowedToTap
                            ? <p className='gfan__controls__tap__Text'>¡Corre! Haz tap en el botón</p>
                            : <p className='gfan__controls__tap__Text'>¡Prepárate para correr!</p>
                        }
                    </>
                )}

                <button
                    className={`gfan__controls__tap ${isPlayerAllowedToTap ? '' : 'gfan__controls__tap--disabled'}`}
                    onClick={handleScreenTap}
                    onTouchStart={handleScreenTap}
                >
                    <img
                        src={buttonImageUrl}
                        className='gfan__controls__tap__image'
                        alt="Botón Tap"
                        draggable="false"
                    />
                </button>
            </div>

            <div className='Fan__Goal__Footer'>
                <p className='FooterCredits'>Hecho con ♥ por Interaktia | 2026</p>
            </div>
        </div>
    );
};

export { GoalV2 };